import { IHttpService } from "../HttpService";
import RoleType, { IRoleType } from "../../model/role/RoleType";
import RoleTypeReportingType, {
  IRoleTypeReportingType
} from "../../model/role/RoleTypeReportingType";
import RoleTypePositionType, {
  IRoleTypePositionType
} from "../../model/role/RoleTypePositionType";
import { formatBooleanValue, parseBooleanValue } from "../..";

export interface IRoleTypeService {
  getRoleTypes(): Promise<IRoleType[]>;
  createRoleType(roleType: IRoleType): Promise<any>;
  updateRoleType(roleType: IRoleType): void;
  getRoleTypeReportingTypes(roleTypeCode: string): Promise<any>;
  removeRoleTypeReportingTypes(ids: string[]): void;
  createRoleTypeReportingTypes(
    roleTypeReportingTypes: IRoleTypeReportingType[]
  ): void;
  getRoleTypePositionTypes(roleTypeCode: string): Promise<any>;
  removeRoleTypePositionTypes(roleTypePositionTypeIds: string[]): void;
  createRoleTypePositionTypes(
    roleTypePositionTypes: IRoleTypePositionType[]
  ): void;
}

const RoleTypeService = (httpService): IRoleTypeService => {
  const _http: IHttpService = httpService;
  const _baseUrl: string = "/api/codebooks";
  const _roleTypesPath = "role-types";

  const _reportingTypesPath = "reporting-types";
  const _positionTypesPath = "position-types";

  const RoleTypeListMapper = (json = []) => {
    return json.map((item: any) => {
      let roleType = RoleType(item);
      roleType.valid = formatBooleanValue(item.valid);
      return roleType;
    });
  };

  const RoleTypeReportingTypeMapper = (json = []) => {
    return json.map((item: any) => {
      return RoleTypeReportingType(item);
    });
  };

  const RoleTypePositionTypeMapper = (json = []) => {
    return json.map((item: any) => {
      return RoleTypePositionType(item);
    });
  };

  let ParamsMapper = (roleType: IRoleType) => {
    return {
      code: roleType.code,
      name: roleType.name,
      valid: parseBooleanValue(roleType.valid),
      description: roleType.description
    };
  };

  return {
    async getRoleTypes() {
      const path = _http.buildPath(_baseUrl, _roleTypesPath);
      const response = await _http.get(path);
      const responseJSON = await _http.toJSON(response);
      return RoleTypeListMapper(responseJSON);
    },

    async createRoleType(roleType: IRoleType) {
      const path = _http.buildPath(_baseUrl, _roleTypesPath);
      const response = await _http.post(path, {
        params: ParamsMapper(roleType)
      });
      return _http.toText(response);
    },

    async updateRoleType(roleType: IRoleType) {
      const path = _http.buildPath(_baseUrl, _roleTypesPath, roleType.code);
      const response = await _http.put(path, {
        params: ParamsMapper(roleType)
      });
      return _http.toText(response);
    },

    async getRoleTypeReportingTypes(roleTypeCode: String) {
      const path = _http.buildPath(
        _baseUrl,
        _roleTypesPath,
        _reportingTypesPath
      );
      const response = await _http.get(path, {
        params: {
          roleTypeCode
        }
      });
      const responseJSON = await _http.toJSON(response);
      return RoleTypeReportingTypeMapper(responseJSON);
    },

    async removeRoleTypeReportingTypes(roleTypeReportingTypeIds: string[]) {
      const path = _http.buildPath(
        _baseUrl,
        _roleTypesPath,
        _reportingTypesPath
      );
      const response = await _http.remove(path, {
        params: roleTypeReportingTypeIds
      });
      return _http.toText(response);
    },

    async createRoleTypeReportingTypes(
      roleTypeReportingTypes: IRoleTypeReportingType[]
    ) {
      const path = _http.buildPath(
        _baseUrl,
        _roleTypesPath,
        _reportingTypesPath
      );
      const response = await _http.post(path, {
        params: roleTypeReportingTypes
      });
      return _http.toText(response);
    },

    async getRoleTypePositionTypes(roleTypeCode: String) {
      const path = _http.buildPath(
        _baseUrl,
        _roleTypesPath,
        _positionTypesPath
      );
      const response = await _http.get(path, {
        params: { roleTypeCode }
      });
      const responseJSON = await _http.toJSON(response);
      return RoleTypePositionTypeMapper(responseJSON);
    },

    async removeRoleTypePositionTypes(roleTypePositionTypeIds: string[]) {
      const path = _http.buildPath(
        _baseUrl,
        _roleTypesPath,
        _positionTypesPath
      );
      const response = await _http.remove(path, {
        params: roleTypePositionTypeIds
      });
      return _http.toText(response);
    },

    async createRoleTypePositionTypes(
      roleTypePositionTypes: IRoleTypePositionType[]
    ) {
      const path = _http.buildPath(
        _baseUrl,
        _roleTypesPath,
        _positionTypesPath
      );
      const response = await _http.post(path, {
        params: roleTypePositionTypes
      });
      return _http.toText(response);
    }
  };
};

export default RoleTypeService;
